export const PROCESS_STATUS = {
  NOT_APPLICABLE: "NOT_APPLICABLE",
  NO_DEFINED_PROCESS: "NO_DEFINED_PROCESS",
  DEFINED_PROCESS_EXISTS: "DEFINED_PROCESS_EXISTS",
};

export const DOCUMENTED_STATUSES = [
  "DOCUMENTED_AND_MAINTAINED",
  "DOCUMENTED_NOT_MAINTAINED",
];

export const createEmptyResponse = () => ({
  answered: false,

  processStatus: "",

  documentationStatus: "",
  storageType: "",
  processFrequency: "",

  hasOwner: null,
  ownerName: "",

  isTrusted: null,
  isInUse: null,
  isReviewedRegularly: null,
  staffTrained: null,

  knownByOnePerson: null,
  knownByPeopleText: "",

  confidenceLevel: "",
  businessImpact: "",

  notes: "",
});

export const groupQuestionsByArea = (questions = []) => {
  return questions.reduce((groups, question) => {
    const area = question.area || "OTHER";

    if (!groups[area]) {
      groups[area] = [];
    }

    groups[area].push(question);

    return groups;
  }, {});
};

export const calculateAuditProgress = ({
  questions = [],
  responses = {},
}) => {
  let assessed = 0;
  let notApplicable = 0;
  let noDefinedProcess = 0;
  let definedProcessExists = 0;

  questions.forEach((question) => {
    const status =
      responses[question.id]?.processStatus;

    if (!status) {
      return;
    }

    assessed += 1;

    switch (status) {
      case PROCESS_STATUS.NOT_APPLICABLE:
        notApplicable += 1;
        break;

      case PROCESS_STATUS.NO_DEFINED_PROCESS:
        noDefinedProcess += 1;
        break;

      case PROCESS_STATUS.DEFINED_PROCESS_EXISTS:
        definedProcessExists += 1;
        break;

      default:
        break;
    }
  });

  return {
    total: questions.length,
    assessed,
    remaining: Math.max(
      questions.length - assessed,
      0,
    ),
    notApplicable,
    noDefinedProcess,
    definedProcessExists,
  };
};

export const normaliseResponseForProcessStatus = ({
  currentResponse,
  nextStatus,
}) => {
  const current = {
    ...createEmptyResponse(),
    ...(currentResponse || {}),
  };

  switch (nextStatus) {
    case PROCESS_STATUS.NOT_APPLICABLE:
      return {
        ...current,

        answered: true,
        processStatus: nextStatus,

        documentationStatus: "",
        storageType: "",
        processFrequency: "",

        hasOwner: null,
        ownerName: "",

        isTrusted: null,
        isInUse: null,
        isReviewedRegularly: null,
        staffTrained: null,

        knownByOnePerson: null,
        knownByPeopleText: "",

        confidenceLevel: "",
        businessImpact: "",
      };

    case PROCESS_STATUS.NO_DEFINED_PROCESS:
      return {
        ...current,

        answered: true,
        processStatus: nextStatus,

        documentationStatus: "",
        storageType: "",

        isTrusted: null,
        isInUse: null,
        isReviewedRegularly: null,
        staffTrained: null,

        confidenceLevel: "",
      };

    case PROCESS_STATUS.DEFINED_PROCESS_EXISTS:
      return {
        ...current,
        answered: true,
        processStatus: nextStatus,
      };

    default:
      return {
        ...createEmptyResponse(),
        notes: current.notes,
      };
  }
};

export const normaliseResponseForDocumentationStatus = ({
  currentResponse,
  documentationStatus,
}) => {
  const current = {
    ...createEmptyResponse(),
    ...(currentResponse || {}),
  };

  const isDocumented =
    DOCUMENTED_STATUSES.includes(
      documentationStatus,
    );

  return {
    ...current,

    answered: true,
    documentationStatus,

    ...(isDocumented
      ? {}
      : {
          storageType: "",
          isTrusted: null,
          isInUse: null,
          isReviewedRegularly: null,
          staffTrained: null,
        }),
  };
};

export const buildOperationalAuditSubmitPayload = ({
  questions = [],
  responses = {},
}) => {
  return questions
    .map((question) => {
      const response = responses[question.id];

      if (!response?.processStatus) {
        return null;
      }

      const isNotApplicable =
        response.processStatus ===
        PROCESS_STATUS.NOT_APPLICABLE;

      const hasDefinedProcess =
        response.processStatus ===
        PROCESS_STATUS.DEFINED_PROCESS_EXISTS;

      const isDocumented =
        hasDefinedProcess &&
        DOCUMENTED_STATUSES.includes(
          response.documentationStatus,
        );

      return {
        auditQuestionId: question.id,

        processStatus: response.processStatus,

        documentationStatus: hasDefinedProcess
          ? response.documentationStatus || null
          : null,

        storageType: isDocumented
          ? response.storageType || null
          : null,

        processFrequency: isNotApplicable
          ? null
          : response.processFrequency || null,

        hasOwner: isNotApplicable
          ? null
          : response.hasOwner,

        ownerName:
          !isNotApplicable &&
          response.hasOwner === true
            ? response.ownerName.trim() || null
            : null,

        isTrusted: isDocumented
          ? response.isTrusted
          : null,

        isInUse: isDocumented
          ? response.isInUse
          : null,

        isReviewedRegularly: isDocumented
          ? response.isReviewedRegularly
          : null,

        staffTrained: isDocumented
          ? response.staffTrained
          : null,

        knownByOnePerson: isNotApplicable
          ? null
          : response.knownByOnePerson,

        knownByPeople: isNotApplicable
          ? []
          : parseKnownByPeople(
              response.knownByPeopleText,
            ),

        confidenceLevel: hasDefinedProcess
          ? response.confidenceLevel || null
          : null,

        businessImpact: isNotApplicable
          ? null
          : response.businessImpact || null,

        notes: response.notes.trim() || null,
      };
    })
    .filter(Boolean);
};

const parseKnownByPeople = (value) => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
};